import { ErrorMsg } from '../../types/ViewModel/ErrorMsg';
import eventtypecommon from './eventtypecommon';
import { TankersEvent } from '../../types/ViewModel/TankersEvent';
import { EventTypeData } from '../../types/ViewModel/EventTypeData';
import { RequiredConditionalMandatory } from '../../types/ViewModel/RequiredConditionalMandatory';
import { IRotationNode } from '../../types/ViewModel/IRotationNode';
import useEventType from '../../app/useEventType';
import useEvent from '../../app/useEvent';
import eventTypeGroupChangeLogUtils from './eventTypeGroupChangeLogUtils';
import { compareActualEventDateTime } from './eventDateUtils';
import { canBypassValidationForRetroactiveCargo, getRetroactiveCargoEventMessage } from './retroactiveCargoEventUtils';

export default () => {
    const { getEventType } = useEventType();
    const { filterSameEventTypeGroupAndLevel } = useEvent();
    const {
        hasEventGroupDependency,
        findMissingEventTypeInSequence,
        isEventOnSelectedNode,
        getParentEvents,
        hasConcurrentParentInCompleteTree,
        generateEventNameWithParentDependency,
        generateEventNameWithDependency,
        sortOrderedEventTypes,
        generateEventNameWithDependencyAndDate,
    } = eventtypecommon();
    const { isMultiple, isConcurrent, getRequiredPreviousTypeId, getNotAfterEventTypeId } = eventTypeGroupChangeLogUtils();

    // Check if event in list could be deleted
    const isDeleteEventTypeValid = (
        eventArray: TankersEvent[],
        node: IRotationNode,
        checkEvent: TankersEvent,
        eventTypeData: EventTypeData,
        isRetroactiveModeEnabled?: boolean
    ): ErrorMsg => {
        const errorMsg: ErrorMsg = new ErrorMsg(false, 'Delete valid', false, null);

        eventArray = eventArray.filter((ev) => !ev.deleted);

        eventArray = getParentEvents(eventArray, node);

        // Find events on selected node
        const eventArrayInContext: TankersEvent[] = eventArray.filter((ev) => {
            return isEventOnSelectedNode(checkEvent, ev);
        });

        // Find events with current context and in event type group
        const eventArrayInGroup = filterSameEventTypeGroupAndLevel(eventArrayInContext, checkEvent.eventType);

        // Find events with same parent
        const eventArraySameParent: TankersEvent[] = eventArrayInGroup.filter((ev: TankersEvent) => {
            return (
                checkEvent.eventTypeGroupSequenceDependencyIdentity &&
                ev.eventTypeGroupSequenceDependencyIdentity &&
                checkEvent.eventTypeGroupSequenceDependencyIdentity === ev.eventTypeGroupSequenceDependencyIdentity
            );
        });

        // Find events with current context, in event type group and with same sequence identity
        const eventArrayInSequence: TankersEvent[] = eventArrayInGroup.filter((ev: TankersEvent) => {
            return ev.eventTypeGroupSequenceIdentity === checkEvent.eventTypeGroupSequenceIdentity;
        });

        // Check if current event has mandatory successor in group
        const successorEvent: TankersEvent | null =
            eventArrayInSequence.find((ev) => checkEvent.eventType && ev.eventTypeId === checkEvent.eventType.nextEventTypeInGroupId) ?? null;
        // If mandatory successor exists, delete event is not valid
        if (successorEvent) {
            // Allow bypass for retroactive cargo event modifications
            if (canBypassValidationForRetroactiveCargo(checkEvent, successorEvent, eventArray, isRetroactiveModeEnabled)) {
                // Continue without error for retroactive cargo modifications
            } else {
                errorMsg.error = true;
                errorMsg.message = generateEventNameWithDependency(successorEvent) + ' must be deleted first';
                errorMsg.notValidatedEvent = successorEvent;
                return errorMsg;
            }
        }

        // Check if other unfinished sequence of same event type group exists, for non-parallel event types
        if (
            checkEvent.eventType &&
            checkEvent.eventType.eventTypeGroup &&
            !isConcurrent(checkEvent.eventType.eventTypeGroup, node.firstTimeInPortCall)
        ) {
            let parentMultiple = false;
            let parentConcurrent = false;
            // Find parent
            const reqPrevTypeId = getRequiredPreviousTypeId(checkEvent.eventType.eventTypeGroup, node.firstTimeInPortCall);
            if (reqPrevTypeId) {
                const parentEvType = getEventType(reqPrevTypeId, null);
                parentMultiple =
                    parentEvType && parentEvType.eventTypeGroup ? isMultiple(parentEvType.eventTypeGroup, node.firstTimeInPortCall) : false;
                parentConcurrent = !!hasConcurrentParentInCompleteTree(checkEvent.eventType, eventTypeData, node);
            }
            if (!parentMultiple) {
                const indSequence = findMissingEventTypeInSequence(eventArrayInGroup);
                if (indSequence !== -1) {
                    // Check sequence identities
                    if (
                        eventArrayInGroup[indSequence].eventType &&
                        eventArrayInGroup[indSequence].eventTypeGroupSequenceIdentity !== checkEvent.eventTypeGroupSequenceIdentity
                    ) {
                        errorMsg.error = true;
                        errorMsg.message =
                            "'" +
                            eventArrayInGroup[indSequence].eventType?.name +
                            "' must be registered before delete as you cannot have several open sequences of '" +
                            checkEvent.eventType.eventTypeGroup.name +
                            "' in the toolbox at the same time";
                        errorMsg.notValidatedEvent = eventArrayInGroup[indSequence];
                        return errorMsg;
                    }
                }
            } else if (parentConcurrent && eventArraySameParent && eventArraySameParent.length > 0) {
                const indSequence = findMissingEventTypeInSequence(eventArraySameParent);
                if (indSequence !== -1) {
                    // Check sequence identities
                    if (eventArraySameParent[indSequence].eventTypeGroupSequenceIdentity !== checkEvent.eventTypeGroupSequenceIdentity) {
                        errorMsg.error = true;
                        errorMsg.message =
                            "'" +
                            generateEventNameWithParentDependency(checkEvent, eventArraySameParent[indSequence]) +
                            "' must be registered before delete as you cannot have several open sequences of '" +
                            checkEvent.eventType.eventTypeGroup.name +
                            "' in the toolbox at the same time";
                        errorMsg.notValidatedEvent = eventArraySameParent[indSequence];
                        return errorMsg;
                    }
                }
            }
        }

        // Check if there are other events (of event type groups) with RequiredPreviousTypeId set to current event (event type id), then delete event is not valid
        // Only check for events that are not dependent on current event
        const requiredPreviousEventType: TankersEvent | null =
            eventArrayInContext.find((ev) => {
                return (
                    ev.eventType &&
                    ev.eventType.eventTypeGroup &&
                    getRequiredPreviousTypeId(ev.eventType.eventTypeGroup, node.firstTimeInPortCall) === checkEvent.eventTypeId &&
                    !ev.eventTypeGroupSequenceDependencyIdentity
                );
            }) ?? null;
        if (requiredPreviousEventType) {
            // Allow bypass for retroactive cargo event modifications
            if (canBypassValidationForRetroactiveCargo(checkEvent, requiredPreviousEventType, eventArray, isRetroactiveModeEnabled)) {
                // Continue without error for retroactive cargo modifications
            } else {
                errorMsg.error = true;
                errorMsg.message = generateEventNameWithDependency(requiredPreviousEventType) + ' must be deleted first';
                errorMsg.notValidatedEvent = requiredPreviousEventType;
                return errorMsg;
            }
        }

        // Check if there are other events (of event type groups) with RequiredPreviousTypeId set to current event (event type id), then delete event is not valid
        // Only check for events that are  dependent on current event
        const requiredPreviousDependentEventType: TankersEvent | null =
            eventArrayInContext.find((ev) => {
                return (
                    ev.eventType &&
                    ev.eventType.eventTypeGroup &&
                    getRequiredPreviousTypeId(ev.eventType.eventTypeGroup, node.firstTimeInPortCall) === checkEvent.eventTypeId &&
                    hasEventGroupDependency(ev, checkEvent)
                );
            }) ?? null;
        if (requiredPreviousDependentEventType) {
            // Allow bypass for retroactive cargo event modifications
            if (canBypassValidationForRetroactiveCargo(checkEvent, requiredPreviousDependentEventType, eventArray, isRetroactiveModeEnabled)) {
                // Continue without error for retroactive cargo modifications
            } else {
                errorMsg.error = true;
                errorMsg.message = generateEventNameWithDependency(requiredPreviousDependentEventType) + ' must be deleted first';
                errorMsg.notValidatedEvent = requiredPreviousDependentEventType;
                return errorMsg;
            }
        }

        // Check if current event's NotAfterEventId set to other events, then delete event is not valid
        // Only check for events that current event are dependent on
        const notAfterDependentEventType: TankersEvent | null =
            eventArrayInContext.find((ev) => {
                const ch1 =
                    checkEvent.eventType &&
                    checkEvent.eventType.eventTypeGroup &&
                    getNotAfterEventTypeId(checkEvent.eventType.eventTypeGroup, node.firstTimeInPortCall) === ev.eventTypeId;
                const ch2 = hasEventGroupDependency(checkEvent, ev);
                return ch1 && ch2;
            }) ?? null;

        if (notAfterDependentEventType) {
            // Allow bypass for retroactive cargo event modifications
            if (canBypassValidationForRetroactiveCargo(checkEvent, notAfterDependentEventType, eventArray, isRetroactiveModeEnabled)) {
                // Continue without error for retroactive cargo modifications
            } else {
                errorMsg.error = true;
                if (notAfterDependentEventType.eventType && notAfterDependentEventType.eventType.eventTypeGroup) {
                    if (isMultiple(notAfterDependentEventType.eventType.eventTypeGroup, node.firstTimeInPortCall)) {
                        errorMsg.message = generateEventNameWithDependency(notAfterDependentEventType) + ' must be deleted first';
                    } else {
                        errorMsg.message = "'" + notAfterDependentEventType.eventType.name + "' must be deleted first";
                    }
                    errorMsg.notValidatedEvent = notAfterDependentEventType;
                }
                return errorMsg;
            }
        }

        // Check if current event type group has mandatory successor in same level and context
        const successorEventInGroup: TankersEvent | null =
            eventArrayInContext.find((ev) => {
                return (
                    ev.eventType &&
                    ev.eventType.eventTypeGroup &&
                    checkEvent.eventType &&
                    checkEvent.eventType.eventTypeGroup &&
                    ev.eventType.eventTypeGroup.eventTypeGroupId === checkEvent.eventType.eventTypeGroup.nextGroupInLevelId
                );
            }) ?? null;
        if (successorEventInGroup) {
            // Check if only one event left in the event type group
            if (eventArrayInGroup.length === 1) {
                // Allow bypass for retroactive cargo event modifications
                if (canBypassValidationForRetroactiveCargo(checkEvent, successorEventInGroup, eventArray, isRetroactiveModeEnabled)) {
                    // Continue without error for retroactive cargo modifications
                } else {
                    errorMsg.error = true;
                    errorMsg.message = generateEventNameWithDependency(successorEventInGroup) + ' must be deleted first';
                    errorMsg.notValidatedEvent = successorEventInGroup;
                    return errorMsg;
                }
            }
        }

        // checkEvent is conditional mandatory, check required conditional mandatory dependent on this
        const evDependentOnCheckEventArray = eventArrayInContext.filter(
            (ev) => checkEvent.eventTypeGroupSequenceIdentity === ev.eventTypeGroupSequenceDependencyIdentity
        );
        if (evDependentOnCheckEventArray && evDependentOnCheckEventArray.length > 0 && checkEvent.eventType && checkEvent.eventType.eventTypeGroup) {
            const eventTypeArraySorted = sortOrderedEventTypes(checkEvent.eventType.eventTypeGroup.eventTypeArray);
            const reqArray: RequiredConditionalMandatory[] = [];
            evDependentOnCheckEventArray.forEach((ev) => {
                if (
                    ev.eventType &&
                    ev.eventType.eventTypeGroup &&
                    ev.eventType.eventTypeGroup.requiredConditionalMandatoryArray &&
                    ev.eventType.eventTypeGroup.requiredConditionalMandatoryArray.length > 0
                ) {
                    ev.eventType.eventTypeGroup.requiredConditionalMandatoryArray.forEach((reqItem) => {
                        const foundReqItem = reqArray.find((req) => req.requiredConditionalMandatoryId === reqItem.requiredConditionalMandatoryId);
                        if (!foundReqItem) {
                            reqArray.push(reqItem);
                        }
                    });
                }
            });
            let reqCondInd = 0;
            let reqCondKeepGoing = reqArray && reqArray.length > 0;
            while (reqCondKeepGoing) {
                const depEvTypeArray = evDependentOnCheckEventArray.filter(
                    (ev) => ev.eventType && ev.eventType.eventTypeGroupId === reqArray[reqCondInd].eventTypeGroupId
                );
                if (depEvTypeArray && depEvTypeArray.length > 0) {
                    const reqPrevIndex = eventTypeArraySorted.findIndex((ev) => ev.eventTypeId === reqArray[reqCondInd].requiredPreviousTypeId);
                    const notAfterIndex = eventTypeArraySorted.findIndex((ev) => ev.eventTypeId === reqArray[reqCondInd].notAfterEventTypeId);
                    const checkEventIndex = eventTypeArraySorted.findIndex((ev) => ev.eventTypeId === checkEvent.eventTypeId);

                    if (
                        reqPrevIndex > -1 &&
                        (notAfterIndex > -1 || !reqArray[reqCondInd].notAfterEventTypeId) &&
                        checkEventIndex > -1 &&
                        checkEventIndex === reqPrevIndex
                    ) {
                        // Allow bypass for retroactive cargo event modifications
                        if (canBypassValidationForRetroactiveCargo(checkEvent, depEvTypeArray[0], eventArray, isRetroactiveModeEnabled)) {
                            // Continue without error for retroactive cargo modifications
                        } else {
                            errorMsg.error = true;
                            errorMsg.message = generateEventNameWithDependency(depEvTypeArray[0]) + ' must be deleted first';
                            errorMsg.notValidatedEvent = depEvTypeArray[0];
                            return errorMsg;
                        }
                    }
                    reqCondInd++;
                } else {
                    reqCondInd++;
                }
                if (reqCondInd === reqArray.length) reqCondKeepGoing = false;
            }
        }

        // checkEvent is required conditional mandatory, check conditional mandatory dependent on this
        const evCheckEventDependentOnArray = eventArrayInContext.filter(
            (ev) => checkEvent.eventTypeGroupSequenceDependencyIdentity === ev.eventTypeGroupSequenceIdentity
        );
        if (
            evCheckEventDependentOnArray &&
            evCheckEventDependentOnArray.length > 0 &&
            checkEvent.eventType &&
            checkEvent.eventType.eventTypeGroup &&
            checkEvent.eventType.eventTypeGroup.requiredConditionalMandatoryArray
        ) {
            checkEvent.eventType.eventTypeGroup.requiredConditionalMandatoryArray.forEach((req) => {
                if (!errorMsg.error) {
                    const notAfterIndex = evCheckEventDependentOnArray.findIndex((ev) => ev.eventTypeId === req.notAfterEventTypeId);
                    if (notAfterIndex > -1) {
                        // Allow bypass for retroactive cargo event modifications
                        if (
                            canBypassValidationForRetroactiveCargo(
                                checkEvent,
                                evCheckEventDependentOnArray[notAfterIndex],
                                eventArray,
                                isRetroactiveModeEnabled
                            )
                        ) {
                            // Continue without error for retroactive cargo modifications
                        } else {
                            errorMsg.error = true;
                            errorMsg.message =
                                generateEventNameWithDependency(evCheckEventDependentOnArray[notAfterIndex]) + ' must be deleted first';
                            errorMsg.notValidatedEvent = evCheckEventDependentOnArray[notAfterIndex];
                        }
                    }
                }
            });
        }
        if (errorMsg.error) {
            return errorMsg;
        }

        // Check if other event types has current group as required group
        const hasRequiredGroup: TankersEvent | null =
            eventArrayInContext.find((ev) => {
                return (
                    ev.eventType &&
                    ev.eventType.eventTypeGroupRequiredArray &&
                    (ev.eventType.eventTypeGroupRequiredArray.find((etgr) => {
                        return checkEvent.eventType && etgr === checkEvent.eventType.eventTypeGroupId;
                    }) ??
                        null)
                );
            }) ?? null;
        if (hasRequiredGroup && hasRequiredGroup.eventType) {
            // Allow bypass for retroactive cargo event modifications
            if (canBypassValidationForRetroactiveCargo(checkEvent, hasRequiredGroup, eventArray, isRetroactiveModeEnabled)) {
                // Continue without error for retroactive cargo modifications
            } else {
                errorMsg.error = true;
                errorMsg.message = "'" + hasRequiredGroup.eventType.name + "' must be deleted first";
                errorMsg.notValidatedEvent = hasRequiredGroup;
                return errorMsg;
            }
        }

        // CHeck alternate event type groups
        // Check if newEvType has entries in AlternateEventTypeGroupMap
        const altEvTypeGrIdList = eventTypeData.alternateEventTypeGroupMapList
            .filter((aetgm) => aetgm.eventTypeGroupId === checkEvent.eventType?.eventTypeGroupId)
            .map((o) => o.alternateEventTypeGroupMapIdentity);
        const altEvTypeGrIdListUnique = altEvTypeGrIdList.filter(function (v, i) {
            return altEvTypeGrIdList.indexOf(v) == i;
        });
        if (altEvTypeGrIdListUnique && altEvTypeGrIdListUnique.length > 0) {
            // For each entry in AlternateEventTypeGroupMap
            let ind = 0;
            let keepGoing = true;
            while (keepGoing) {
                const altEvTypeGroupMapList = eventTypeData.alternateEventTypeGroupMapList.filter(
                    (o) => o.alternateEventTypeGroupMapIdentity === altEvTypeGrIdListUnique[ind]
                );
                if (altEvTypeGroupMapList && altEvTypeGroupMapList.length > 0) {
                    const evList = eventArray
                        .filter((ev) => {
                            return altEvTypeGroupMapList.find((o) => o.eventTypeGroupId === ev.eventType?.eventTypeGroupId);
                        })
                        .sort((a, b) => compareActualEventDateTime(a, b, false));
                    if (evList && evList.length > 0) {
                        const evInd = evList.findIndex((ev) => ev.eventId === checkEvent.eventId);
                        if (evInd > -1 && evInd < evList.length - 1) {
                            // Allow bypass for retroactive cargo event modifications
                            if (canBypassValidationForRetroactiveCargo(checkEvent, evList[evInd + 1], eventArray, isRetroactiveModeEnabled)) {
                                // Continue without error for retroactive cargo modifications
                            } else {
                                errorMsg.error = true;
                                errorMsg.message = "'" + generateEventNameWithDependencyAndDate(evList[evInd + 1]) + "' must be deleted first";
                                errorMsg.notValidatedEvent = hasRequiredGroup;
                                return errorMsg;
                            }
                        }
                    }
                    ind++;
                } else {
                    ind++;
                }
                if (ind === altEvTypeGrIdListUnique.length) keepGoing = false;
            }
        }

        // Check parent to alternate event type groups
        const altEvTypeGrIdListToInitial = eventTypeData.alternateEventTypeGroupMapList
            .filter(
                (aetgm) => aetgm.initialExcludeEventTypeGroupId && aetgm.initialExcludeEventTypeGroupId === checkEvent.eventType?.eventTypeGroupId
            )
            .map((o) => o.alternateEventTypeGroupMapIdentity);
        const altEvTypeGrIdListUniqueInitial = altEvTypeGrIdListToInitial.filter(function (v, i) {
            return altEvTypeGrIdListToInitial.indexOf(v) == i;
        });
        if (altEvTypeGrIdListUniqueInitial && altEvTypeGrIdListUniqueInitial.length > 0) {
            // For each entry in AlternateEventTypeGroupMap
            let indInitial = 0;
            let keepGoingInitial = true;
            while (keepGoingInitial) {
                const altEvTypeGroupMapList = eventTypeData.alternateEventTypeGroupMapList.filter(
                    (o) => o.alternateEventTypeGroupMapIdentity === altEvTypeGrIdListUniqueInitial[indInitial] && o.initialExcludeEventTypeGroupId
                );
                if (altEvTypeGroupMapList && altEvTypeGroupMapList.length > 0) {
                    const evList = eventArray
                        .filter((ev) => {
                            return altEvTypeGroupMapList.find((o) => o.eventTypeGroupId === ev.eventType?.eventTypeGroupId);
                        })
                        .sort((a, b) => compareActualEventDateTime(a, b, false));
                    if (evList && evList.length > 0) {
                        // Allow bypass for retroactive cargo event modifications
                        if (canBypassValidationForRetroactiveCargo(checkEvent, evList[0], eventArray, isRetroactiveModeEnabled)) {
                            // Continue without error for retroactive cargo modifications
                        } else {
                            errorMsg.error = true;
                            errorMsg.message = "'" + generateEventNameWithDependencyAndDate(evList[0]) + "' must be deleted first";
                            errorMsg.notValidatedEvent = hasRequiredGroup;
                            return errorMsg;
                        }
                    }
                    indInitial++;
                } else {
                    indInitial++;
                }
                if (indInitial === altEvTypeGrIdListUniqueInitial.length) keepGoingInitial = false;
            }
        }

        // checkEvent = Protest
        if (
            getRequiredPreviousTypeId(checkEvent.eventType?.eventTypeGroup, node.firstTimeInPortCall) &&
            !getNotAfterEventTypeId(checkEvent.eventType?.eventTypeGroup, node.firstTimeInPortCall)
        ) {
            const reqParentOnlyAfter = eventArrayInContext.find((ev) => {
                return (
                    getRequiredPreviousTypeId(checkEvent.eventType?.eventTypeGroup, node.firstTimeInPortCall) === ev.eventTypeId &&
                    checkEvent.eventTypeGroupSequenceDependencyIdentity &&
                    checkEvent.eventTypeGroupSequenceDependencyIdentity === ev.eventTypeGroupSequenceIdentity
                );
            });
            // reqParentOnlyAfter = Pumping complete
            if (reqParentOnlyAfter && getNotAfterEventTypeId(reqParentOnlyAfter.eventType?.eventTypeGroup, node.firstTimeInPortCall)) {
                // Find cargo hose off
                const notAfterEvRequired = eventArrayInContext.find((ev) => {
                    return (
                        ev.eventTypeId === getNotAfterEventTypeId(reqParentOnlyAfter.eventType?.eventTypeGroup, node.firstTimeInPortCall) &&
                        reqParentOnlyAfter.eventTypeGroupSequenceDependencyIdentity &&
                        reqParentOnlyAfter.eventTypeGroupSequenceDependencyIdentity === ev.eventTypeGroupSequenceIdentity
                    );
                });
                if (notAfterEvRequired) {
                    // Allow bypass for retroactive cargo event modifications
                    if (canBypassValidationForRetroactiveCargo(checkEvent, notAfterEvRequired, eventArray, isRetroactiveModeEnabled)) {
                        // Continue without error for retroactive cargo modifications
                    } else {
                        errorMsg.error = true;
                        errorMsg.message = "'" + generateEventNameWithDependencyAndDate(notAfterEvRequired) + "' must be deleted first";
                        errorMsg.notValidatedEvent = notAfterEvRequired;
                        return errorMsg;
                    }
                }
            }
        }
        return errorMsg;
    };

    return {
        isDeleteEventTypeValid,
    };
};
